import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Json } from '@/integrations/supabase/types';

export interface EffectiveFeatureRow {
  feature_key: string;
  effective_enabled: boolean;
  effective_limit_value: number | null;
  source_kind: string; // 'override' | 'producer' | 'plan' | 'missing'
  reason_sv: string;
  label_sv: string;
  description_sv: string | null;
  debug_json: Json;
}

/**
 * Hook for admin panel: fetches effective features via RPC
 * and provides toggles that write to producer_feature_overrides.
 */
export function useAdminEffectiveFeatures(producerId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ['admin-effective-features', producerId];

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<EffectiveFeatureRow[]> => {
      if (!producerId) return [];
      const { data, error } = await supabase.rpc('get_producer_effective_features', {
        p_producer_id: producerId,
      });
      if (error) throw error;
      return (data ?? []) as unknown as EffectiveFeatureRow[];
    },
    enabled: !!producerId,
    staleTime: 10_000,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['producer-effective-features', producerId] });
  };

  const toggleMutation = useMutation({
    mutationFn: async ({ featureKey, enabled }: { featureKey: string; enabled: boolean }) => {
      if (!producerId) throw new Error('No producerId');
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('producer_feature_overrides')
        .upsert(
          {
            producer_id: producerId,
            feature_key: featureKey,
            enabled,
            created_by: user?.id ?? null,
            note: 'Admin-toggle',
          },
          { onConflict: 'producer_id,feature_key' }
        );
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      invalidateAll();
      toast({
        title: variables.enabled ? 'Feature aktiverad' : 'Feature inaktiverad',
        description: 'Override sparad.',
      });
    },
    onError: (err: any) => {
      toast({ title: 'Kunde inte ändra', description: err?.message || 'Försök igen.', variant: 'destructive' });
    },
  });

  const setLimitOverride = useMutation({
    mutationFn: async ({ featureKey, limitValue }: { featureKey: string; limitValue: number | null }) => {
      if (!producerId) throw new Error('No producerId');
      const { data: { user } } = await supabase.auth.getUser();
      // Upsert with limit_value; keep current enabled state or default true
      const currentFeature = query.data?.find(f => f.feature_key === featureKey);
      const { error } = await supabase
        .from('producer_feature_overrides')
        .upsert(
          {
            producer_id: producerId,
            feature_key: featureKey,
            enabled: currentFeature?.effective_enabled ?? true,
            limit_value: limitValue,
            created_by: user?.id ?? null,
            note: limitValue != null ? `Limit override: ${limitValue}` : 'Limit removed',
          },
          { onConflict: 'producer_id,feature_key' }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Limit uppdaterad' });
    },
    onError: (err: any) => {
      toast({ title: 'Fel', description: err?.message || 'Försök igen.', variant: 'destructive' });
    },
  });

  const removeOverride = useMutation({
    mutationFn: async (featureKey: string) => {
      if (!producerId) throw new Error('No producerId');
      const { error } = await supabase
        .from('producer_feature_overrides')
        .delete()
        .eq('producer_id', producerId)
        .eq('feature_key', featureKey);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Override borttagen', description: 'Faller tillbaka till plan.' });
    },
    onError: (err: any) => {
      toast({ title: 'Fel', description: err?.message || 'Försök igen.', variant: 'destructive' });
    },
  });

  return {
    features: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    toggleFeature: (featureKey: string, enabled: boolean) =>
      toggleMutation.mutateAsync({ featureKey, enabled }),
    setLimitOverride: (featureKey: string, limitValue: number | null) =>
      setLimitOverride.mutateAsync({ featureKey, limitValue }),
    removeOverride: (featureKey: string) => removeOverride.mutateAsync(featureKey),
    isToggling: toggleMutation.isPending ? toggleMutation.variables?.featureKey ?? null : null,
    isSettingLimit: setLimitOverride.isPending,
  };
}
